import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiKeysApi, type ApiKey } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SETTINGS_BUTTONS } from "./SettingsLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { cn, timeAgo } from "@/lib/utils";

const API_BASE = "https://api.peersheep.com";
const DOCS_URL = "https://peersheep.com/docs/api";

// D1 returns "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker
const utc = (s: string) => (s.includes("T") ? s : s.replace(" ", "T") + "Z");

export function ApiKeysTab() {
  const { user, workspace } = useAuth();
  const isOwner = workspace?.ownerId === user?.id;

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"read" | "write">("read");
  const [isCreating, setIsCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  useEffect(() => {
    apiKeysApi.list().then((res) => {
      if ("data" in res && res.data) setKeys(res.data);
      setIsLoading(false);
    });
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    const res = await apiKeysApi.create(name.trim(), scope);
    if ("data" in res && res.data) {
      const { key, ...rest } = res.data;
      setKeys((prev) => [{ ...rest, lastUsedAt: null, createdBy: user?.email ?? "" }, ...prev]);
      setNewKey(key);
      setName("");
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsCreating(false);
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    setIsRevoking(true);
    const res = await apiKeysApi.revoke(revoking.id);
    if ("data" in res) {
      setKeys((prev) => prev.filter((k) => k.id !== revoking.id));
      toast.success(`Revoked "${revoking.name}"`);
      setRevoking(null);
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsRevoking(false);
  };

  const copy = (text: string, what: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  };

  return (
    <div className="space-y-4">
      {/* Create */}
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <CardTitle>Create an API key</CardTitle>
          <CardDescription>
            Use Peersheep from your own code or an AI assistant.{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2">Read the docs</a>
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Name</Label>
            <Input
              placeholder="e.g. Claude, Zapier, Internal dashboard"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              disabled={!isOwner}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Access</Label>
            <div className="grid grid-cols-2 gap-2">
              {([
                ["read", "Read only", "View competitors, pages, alerts and stats"],
                ["write", "Read and write", "Also add competitors and pages, trigger crawls, manage alerts"],
              ] as const).map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  disabled={!isOwner}
                  onClick={() => setScope(value)}
                  className={cn(
                    "rounded-md border px-3 py-2.5 text-left transition-colors disabled:opacity-50",
                    scope === value ? "border-foreground bg-muted" : "border-border hover:bg-muted/50",
                  )}
                >
                  <p className="font-sans text-xs font-medium">{label}</p>
                  <p className="font-sans text-[11px] text-muted-foreground mt-0.5">{hint}</p>
                </button>
              ))}
            </div>
          </div>
          <Button onClick={handleCreate} disabled={isCreating || !name.trim() || !isOwner}>
            {isCreating
              ? <Icon name="refresh" size={12} className="animate-spin" />
              : <><Icon name="key-01" size={12} className="mr-2" />Create key</>}
          </Button>
          {!isOwner && (
            <p className="font-sans text-xs text-muted-foreground">Only workspace owners can create and revoke API keys.</p>
          )}
        </CardContent>
      </Card>

      {/* Keys */}
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <CardTitle>{isLoading ? "API keys" : `${keys.length} API key${keys.length !== 1 ? "s" : ""}`}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="px-5 py-4 space-y-2">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
          ) : keys.length === 0 ? (
            <p className="px-5 py-4 font-sans text-xs text-muted-foreground">No API keys yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-xs font-medium truncate">{k.name}</p>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">
                      {k.prefix}… · {k.lastUsedAt ? `used ${timeAgo(utc(k.lastUsedAt))}` : "never used"} · created by {k.createdBy}
                    </p>
                  </div>
                  <Badge variant={k.scope === "write" ? "default" : "secondary"}>
                    {k.scope === "write" ? "read & write" : "read only"}
                  </Badge>
                  {isOwner && (
                    <Button
                      variant="ghost"
                       size="icon-sm" className="text-muted-foreground hover:text-destructive"
                      onClick={() => setRevoking(k)}
                      aria-label={`Revoke ${k.name}`}
                    >
                      <Icon name="delete-02" size={13} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect */}
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <CardTitle>Endpoints</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {([
            ["REST API", `${API_BASE}/v1`],
            ["MCP server", `${API_BASE}/mcp`],
          ] as const).map(([label, url]) => (
            <div key={label} className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
              <div className="flex gap-2">
                <Input value={url} readOnly className="font-mono text-[11px] text-muted-foreground" />
                <Button variant="outline" size="sm" onClick={() => copy(url, label)} aria-label={`Copy ${label} URL`}>
                  <Icon name="copy-01" size={13} />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Show-once dialog */}
      <Dialog open={!!newKey} onOpenChange={(open) => !open && setNewKey(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Copy your API key</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-xs text-muted-foreground">
            This is the only time the full key is shown. Store it somewhere safe, like a password manager or your app's secrets.
          </p>
          <div className="flex gap-2">
            <Input value={newKey ?? ""} readOnly className="font-mono text-[11px]" onFocus={(e) => e.target.select()} />
            <Button variant="outline" size="sm" onClick={() => newKey && copy(newKey, "API key")} aria-label="Copy API key">
              <Icon name="copy-01" size={13} />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog open={!!revoking} onOpenChange={(open) => !open && setRevoking(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Revoke "{revoking?.name}"?</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-xs text-muted-foreground">
            Anything using this key will stop working immediately. This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={isRevoking}>
              {isRevoking ? <Icon name="refresh" size={12} className="animate-spin" /> : "Revoke key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
